import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Loader2, Plus, Save } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/supabaseClient';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import SettingsLayout from '@/components/settings/SettingsLayout';
import { useEffectiveMe } from '@/lib/effectiveMe';
import { hasCap } from '@/lib/capabilities';

const EMPTY_FORM = {
  tenant_id: '',
  nome_configurazione: '',
  fonte: '',
  tipo_fonte: 'CCNL',
  ccnl: '',
  codice_ccnl: '',
  giorni_annui: '30',
  criterio_conteggio: '5_GIORNI',
  settimana_lavorativa: '5',
  data_inizio_validita: new Date().toISOString().slice(0, 10),
  data_fine_validita: '',
  descrizione: '',
  note: '',
  calendario_id: '',
};

const EMPTY_CALENDAR = {
  nome: '',
  ambito: 'Italia',
  data_inizio_validita: `${new Date().getFullYear()}-01-01`,
  data_fine_validita: `${new Date().getFullYear()}-12-31`,
  giorni_lavorativi: '0,1,2,3,4',
};

const EMPTY_HOLIDAY = {
  data: '',
  descrizione: '',
  fonte: 'CONFIGURAZIONE_TENANT',
  tipo: 'FESTIVITA',
  ambito: 'Italia',
  escludi_dal_conteggio: true,
};

const sourceLabels = {
  CCNL: 'CCNL',
  CONTRATTO_INDIVIDUALE: 'Contratto individuale',
  ACCORDO_INDIVIDUALE: 'Accordo individuale',
  CONFIGURAZIONE_TENANT: 'Configurazione tenant',
};

function formatDate(value) {
  if (!value) return 'Senza scadenza';
  return new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium' }).format(new Date(`${value}T00:00:00`));
}

function configuredValue(value, empty = 'Non configurato') {
  return value === undefined || value === null || value === '' ? empty : String(value);
}

function policyRuleSummary(policy, calendars = []) {
  const accrual = policy.regolaMaturazione || {};
  const carry = policy.regolaRiporto || {};
  const expiry = policy.regolaScadenza || {};
  const customDays = accrual.giorniLavorativi || accrual.giorniSettimana;
  const holidays = accrual.giorniFestivi;
  const expiryValue = expiry.dataScadenza
    ?? expiry.scadenza
    ?? carry.dataScadenza
    ?? carry.scadenza;
  const expiryConfigured = Object.keys(expiry).length > 0
    || ['dataScadenza', 'scadenza', 'giorniValidita', 'mesiValidita'].some((key) => key in carry);
  return {
    calendar: policy.criterioConteggio === 'PERSONALIZZATO'
      ? (Array.isArray(customDays) ? `Giorni ${customDays.join(', ')}` : 'Personalizzato non definito')
      : policy.settimanaLavorativa
        ? `${policy.settimanaLavorativa} giorni`
        : policy.criterioConteggio,
    calendarName: policy.calendarioId
      ? calendars.find((calendar) => calendar.id === policy.calendarioId)?.nome || 'Calendario configurato'
      : 'Nessun calendario associato',
    holidays: Array.isArray(holidays) && holidays.length
      ? `${holidays.length} date configurate`
      : 'Non configurate',
    carry: Object.keys(carry).length ? 'Configurato' : 'Non configurato',
    cap: configuredValue(carry.capGiorni ?? carry.maxGiorni ?? carry.cap, Object.keys(carry).length ? 'Non definito' : 'Non configurato'),
    expiry: expiryValue || (expiryConfigured ? 'Configurata · formato da definire' : 'Non configurata'),
    accrual: configuredValue(accrual.maturazioneMensile ?? accrual.monthlyRate, 'Mensile da giorni annui'),
  };
}

export default function ImpostazioniFeriePolicy() {
  const me = useEffectiveMe();
  const canEdit = hasCap(me, 'ferie.config.manage');
  const [policies, setPolicies] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [calendarForm, setCalendarForm] = useState(EMPTY_CALENDAR);
  const [holidayDrafts, setHolidayDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [calendarSaving, setCalendarSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openCalendarForm, setOpenCalendarForm] = useState(false);
  const [error, setError] = useState('');

  const tenantIds = useMemo(() => {
    const values = me?.tenant_ids || me?.tenantIds || [];
    return values.map(String);
  }, [me]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [rows, calendarRows] = await Promise.all([
        apiFetch('/api/ferie/policy'),
        apiFetch('/api/ferie/calendari'),
      ]);
      setPolicies(rows || []);
      setCalendars(calendarRows || []);
      const firstTenant = rows?.[0]?.tenantId || me?.active_tenant_id || me?.activeTenantId || tenantIds[0] || '';
      setForm((current) => ({ ...current, tenant_id: current.tenant_id || firstTenant }));
    } catch (loadError) {
      setError(loadError.message || 'Impossibile caricare le policy ferie.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // The effective identity is stable for this screen; reload only when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const updateCalendar = (key, value) => setCalendarForm((current) => ({ ...current, [key]: value }));
  const updateHoliday = (calendarId, key, value) => setHolidayDrafts((current) => ({
    ...current,
    [calendarId]: { ...(current[calendarId] || EMPTY_HOLIDAY), [key]: value },
  }));

  const save = async (event) => {
    event.preventDefault();
    if (!form.nome_configurazione.trim() || !form.fonte.trim() || !form.giorni_annui || !form.data_inizio_validita) {
      toast.error('Completa nome, fonte, giorni annui e data di validità.');
      return;
    }
    setSaving(true);
    try {
      await apiFetch('/api/ferie/policy', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          tenant_id: form.tenant_id || undefined,
          giorni_annui: Number(form.giorni_annui),
          settimana_lavorativa: form.settimana_lavorativa ? Number(form.settimana_lavorativa) : null,
          data_fine_validita: form.data_fine_validita || null,
          regola_maturazione: { tipo: 'mensile' },
          regola_riporto: {},
          regola_scadenza: {},
          calendario_id: form.calendario_id || undefined,
        }),
      });
      toast.success('Policy ferie salvata', { description: 'La nuova configurazione sarà applicata dalla data indicata.' });
      setForm((current) => ({ ...EMPTY_FORM, tenant_id: current.tenant_id }));
      setOpenForm(false);
      await load();
    } catch (saveError) {
      toast.error(saveError.message || 'Impossibile salvare la policy ferie.');
    } finally {
      setSaving(false);
    }
  };

  const saveCalendar = async (event) => {
    event.preventDefault();
    setCalendarSaving(true);
    try {
      const year = Number(calendarForm.data_inizio_validita.slice(0, 4));
      await apiFetch('/api/ferie/calendari', {
        method: 'POST',
        body: JSON.stringify({
          ...calendarForm,
          anno: year,
          fonte: 'TENANT',
          tenant_id: form.tenant_id || undefined,
          giorni_lavorativi: calendarForm.giorni_lavorativi
            .split(',')
            .map((value) => Number(value.trim()))
            .filter((value) => Number.isInteger(value)),
        }),
      });
      toast.success('Calendario ferie salvato');
      setCalendarForm(EMPTY_CALENDAR);
      setOpenCalendarForm(false);
      await load();
    } catch (saveError) {
      toast.error(saveError.message || 'Impossibile salvare il calendario ferie.');
    } finally {
      setCalendarSaving(false);
    }
  };

  const saveHoliday = async (calendar) => {
    const draft = holidayDrafts[calendar.id] || EMPTY_HOLIDAY;
    if (!draft.data || !draft.descrizione.trim()) {
      toast.error('Indica data e descrizione della festività.');
      return;
    }
    setCalendarSaving(true);
    try {
      await apiFetch(`/api/ferie/calendari/${calendar.id}/festivita`, {
        method: 'POST',
        body: JSON.stringify(draft),
      });
      toast.success('Festività aggiunta');
      setHolidayDrafts((current) => ({ ...current, [calendar.id]: EMPTY_HOLIDAY }));
      await load();
    } catch (saveError) {
      toast.error(saveError.message || 'Impossibile salvare la festività.');
    } finally {
      setCalendarSaving(false);
    }
  };

  return (
    <SettingsLayout
      eyebrow="Impostazioni · Ferie"
      title="Policy ferie"
      description="Configura la regola applicabile al tenant. Le policy individuali valide hanno priorità; senza una policy nuova resta attiva la configurazione esistente."
      breadcrumbs={[{ label: 'Impostazioni', to: '/impostazioni' }]}
      current="Policy ferie"
      navigation={[
        { to: '/impostazioni', label: 'Tutte le impostazioni' },
        { to: '/impostazioni/ferie-fabbisogno', label: 'Fabbisogno minimo' },
      ]}
      testId="impostazioni-ferie-policy"
    >
      {!canEdit && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Puoi consultare le policy, ma non hai il permesso per modificarle.
        </div>
      )}

      <section className="rounded-2xl border border-app-border bg-app-surface p-4 shadow-sm sm:p-6" aria-labelledby="calendar-list-title" data-testid="leave-calendar-section">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">Calendario ferie</div>
            <h2 id="calendar-list-title" className="mt-1 font-display text-xl font-semibold text-brand-navy">Calendari applicabili</h2>
            <p className="mt-1 text-sm text-stone-500">Festività e giorni lavorativi sono dati configurati dal server. Se non esiste un calendario, non vengono inventate festività.</p>
          </div>
          {canEdit && (
            <Button type="button" onClick={() => setOpenCalendarForm((current) => !current)} className="gap-2 bg-brand-green text-white hover:bg-brand-green-hover">
              <Plus className="h-4 w-4" /> Nuovo calendario
            </Button>
          )}
        </div>
        {!loading && !calendars.length && (
          <div className="mt-5 rounded-xl border border-dashed border-stone-300 bg-stone-50 p-5 text-sm text-stone-600">
            Nessun calendario strutturato configurato per l&apos;anno corrente.
          </div>
        )}
        <div className="mt-5 grid min-w-0 gap-4">
          {calendars.map((calendar) => {
            const draft = holidayDrafts[calendar.id] || EMPTY_HOLIDAY;
            return (
              <article key={calendar.id} className="min-w-0 rounded-xl border border-stone-200 bg-white p-4" data-testid={`leave-calendar-${calendar.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-brand-navy">{calendar.nome}</h3>
                    <p className="mt-1 text-sm text-stone-500">
                      {calendar.anno} · Fonte {calendar.fonte} · Ambito {calendar.ambito}
                    </p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${calendar.active ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
                    {calendar.active ? 'Attivo' : 'Inattivo'}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Validità</div><div className="mt-1 font-semibold text-brand-navy">{formatDate(calendar.dataInizioValidita)} → {formatDate(calendar.dataFineValidita)}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Giorni lavorativi</div><div className="mt-1 font-semibold text-brand-navy">{calendar.giorniLavorativi?.length ? calendar.giorniLavorativi.join(', ') : 'Non configurati'}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Festività configurate</div><div className="mt-1 font-semibold text-brand-navy">{calendar.festivitaConfigurate}</div></div>
                </div>
                <div className="mt-4 overflow-x-auto rounded-lg border border-stone-100">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-stone-50 text-stone-500">
                      <tr><th className="px-3 py-2 font-semibold">Data</th><th className="px-3 py-2 font-semibold">Descrizione</th><th className="px-3 py-2 font-semibold">Fonte / ambito</th><th className="px-3 py-2 font-semibold">Stato</th></tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {calendar.festivita?.map((holiday) => (
                        <tr key={holiday.id}>
                          <td className="whitespace-nowrap px-3 py-2">{formatDate(holiday.data)}</td>
                          <td className="px-3 py-2">{holiday.descrizione}</td>
                          <td className="px-3 py-2">{holiday.fonte} · {holiday.ambito}</td>
                          <td className="whitespace-nowrap px-3 py-2">{holiday.attiva ? (holiday.escludiDalConteggio ? 'Attiva · esclusa' : 'Attiva · conteggiata') : 'Inattiva'}</td>
                        </tr>
                      ))}
                      {!calendar.festivita?.length && <tr><td colSpan="4" className="px-3 py-3 text-center text-stone-500">Nessuna festività configurata.</td></tr>}
                    </tbody>
                  </table>
                </div>
                {canEdit && (
                  <div className="mt-4 grid gap-2 rounded-lg border border-dashed border-stone-200 bg-stone-50 p-3 sm:grid-cols-5">
                    <Input aria-label={`Data festività ${calendar.nome}`} type="date" value={draft.data} onChange={(event) => updateHoliday(calendar.id, 'data', event.target.value)} />
                    <Input aria-label={`Descrizione festività ${calendar.nome}`} placeholder="Descrizione" value={draft.descrizione} onChange={(event) => updateHoliday(calendar.id, 'descrizione', event.target.value)} />
                    <Input aria-label={`Fonte festività ${calendar.nome}`} placeholder="Fonte" value={draft.fonte} onChange={(event) => updateHoliday(calendar.id, 'fonte', event.target.value)} />
                    <Input aria-label={`Ambito festività ${calendar.nome}`} placeholder="Ambito" value={draft.ambito} onChange={(event) => updateHoliday(calendar.id, 'ambito', event.target.value)} />
                    <Button type="button" disabled={calendarSaving} onClick={() => saveHoliday(calendar)} className="bg-brand-navy text-white hover:bg-brand-navy/90">Aggiungi festività</Button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {openCalendarForm && canEdit && (
        <form onSubmit={saveCalendar} className="rounded-2xl border border-brand-green/25 bg-white p-4 shadow-sm sm:p-6" data-testid="leave-calendar-form">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-brand-green" />
            <h2 className="font-display text-xl font-semibold text-brand-navy">Nuovo calendario tenant</h2>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="calendar-name">Nome calendario</Label><Input id="calendar-name" value={calendarForm.nome} onChange={(event) => updateCalendar('nome', event.target.value)} placeholder="Calendario ferie 2026" required /></div>
            <div className="space-y-1.5"><Label htmlFor="calendar-scope">Ambito</Label><Input id="calendar-scope" value={calendarForm.ambito} onChange={(event) => updateCalendar('ambito', event.target.value)} placeholder="Italia / sede" required /></div>
            <div className="space-y-1.5"><Label htmlFor="calendar-start">Valido dal</Label><Input id="calendar-start" type="date" value={calendarForm.data_inizio_validita} onChange={(event) => updateCalendar('data_inizio_validita', event.target.value)} required /></div>
            <div className="space-y-1.5"><Label htmlFor="calendar-end">Valido fino al</Label><Input id="calendar-end" type="date" value={calendarForm.data_fine_validita} onChange={(event) => updateCalendar('data_fine_validita', event.target.value)} required /></div>
            <div className="space-y-1.5 md:col-span-2"><Label htmlFor="calendar-weekdays">Giorni lavorativi (0=lunedì, separati da virgole)</Label><Input id="calendar-weekdays" value={calendarForm.giorni_lavorativi} onChange={(event) => updateCalendar('giorni_lavorativi', event.target.value)} placeholder="0,1,2,3,4" /></div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button type="submit" disabled={calendarSaving} className="gap-2 bg-brand-green text-white hover:bg-brand-green-hover">{calendarSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva calendario</Button>
            <Button type="button" variant="outline" onClick={() => setOpenCalendarForm(false)} disabled={calendarSaving}>Annulla</Button>
          </div>
        </form>
      )}

      <section className="rounded-2xl border border-app-border bg-app-surface p-4 shadow-sm sm:p-6" aria-labelledby="policy-list-title">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">Configurazione effettiva</div>
            <h2 id="policy-list-title" className="mt-1 font-display text-xl font-semibold text-brand-navy">Policy tenant versionate</h2>
          </div>
          {canEdit && (
            <Button type="button" onClick={() => setOpenForm((current) => !current)} className="gap-2 bg-brand-green text-white hover:bg-brand-green-hover">
              <Plus className="h-4 w-4" /> Nuova versione
            </Button>
          )}
        </div>

        {loading && <div className="flex items-center gap-2 py-8 text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento policy…</div>}
        {!loading && error && <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}
        {!loading && !error && !policies.length && (
          <div className="mt-5 rounded-xl border border-dashed border-stone-300 bg-stone-50 p-5 text-sm text-stone-600">
            Non esiste ancora una policy nuova. Il motore usa il fallback legacy finché non viene pubblicata una configurazione.
          </div>
        )}
        {!loading && !error && policies.length > 0 && (
          <div className="mt-5 grid gap-3">
            {policies.map((policy) => {
              const summary = policyRuleSummary(policy, calendars);
              return (
              <article key={policy.id} className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`leave-policy-${policy.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-brand-navy">{policy.nomeConfigurazione}</h3>
                    <p className="mt-1 text-sm text-stone-500">{sourceLabels[policy.tipoFonte] || policy.fonte}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${policy.active ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
                    {policy.active ? 'Attiva' : 'Archiviata'}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-4">
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Giorni annui</div><div className="mt-1 font-semibold text-brand-navy">{policy.giorniAnnui}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Conteggio</div><div className="mt-1 font-semibold text-brand-navy">{policy.criterioConteggio}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Valida dal</div><div className="mt-1 font-semibold text-brand-navy">{formatDate(policy.dataInizioValidita)}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Valida fino al</div><div className="mt-1 font-semibold text-brand-navy">{formatDate(policy.dataFineValidita)}</div></div>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Calendario</div><div className="mt-1 font-semibold text-brand-navy">{summary.calendar}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Calendario applicato</div><div className="mt-1 font-semibold text-brand-navy">{summary.calendarName}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Festività</div><div className="mt-1 font-semibold text-brand-navy">{summary.holidays}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Maturazione</div><div className="mt-1 font-semibold text-brand-navy">{summary.accrual}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Riporto</div><div className="mt-1 font-semibold text-brand-navy">{summary.carry}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Cap riporto</div><div className="mt-1 font-semibold text-brand-navy">{summary.cap}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Scadenza</div><div className="mt-1 font-semibold text-brand-navy">{summary.expiry}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-stone-400">Fonte</div><div className="mt-1 font-semibold text-brand-navy">{configuredValue(policy.fonte)}</div></div>
                </div>
                {policy.ccnl || policy.codiceCcnl || policy.descrizione ? (
                  <p className="mt-3 text-xs leading-relaxed text-stone-500">
                    {policy.ccnl || policy.codiceCcnl ? `Riferimento: ${[policy.ccnl, policy.codiceCcnl].filter(Boolean).join(' · ')}` : ''}
                    {policy.descrizione ? `${policy.ccnl || policy.codiceCcnl ? ' — ' : ''}${policy.descrizione}` : ''}
                  </p>
                ) : null}
              </article>
              );
            })}
          </div>
        )}
      </section>

      {openForm && canEdit && (
        <form onSubmit={save} className="rounded-2xl border border-brand-green/25 bg-white p-4 shadow-sm sm:p-6" data-testid="leave-policy-form">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-brand-green" />
            <h2 className="font-display text-xl font-semibold text-brand-navy">Nuova policy tenant</h2>
          </div>
          <p className="mt-1 text-sm text-stone-500">Inserisci la fonte documentale reale. I valori legali non vengono inventati né applicati automaticamente.</p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {tenantIds.length > 1 && (
              <div className="space-y-1.5">
                <Label htmlFor="policy-tenant">Tenant</Label>
                <select id="policy-tenant" value={form.tenant_id} onChange={(event) => update('tenant_id', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">Seleziona tenant</option>
                  {tenantIds.map((tenantId) => <option key={tenantId} value={tenantId}>{tenantId}</option>)}
                </select>
              </div>
            )}
            <div className="space-y-1.5"><Label htmlFor="policy-name">Nome configurazione</Label><Input id="policy-name" value={form.nome_configurazione} onChange={(event) => update('nome_configurazione', event.target.value)} placeholder="Ferie 2027 · CCNL applicato" required /></div>
            <div className="space-y-1.5"><Label htmlFor="policy-source">Fonte documentale</Label><Input id="policy-source" value={form.fonte} onChange={(event) => update('fonte', event.target.value)} placeholder="CCNL / contratto / accordo" required /></div>
            <div className="space-y-1.5"><Label htmlFor="policy-source-type">Tipo fonte</Label><select id="policy-source-type" value={form.tipo_fonte} onChange={(event) => update('tipo_fonte', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">{Object.entries(sourceLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
            <div className="space-y-1.5"><Label htmlFor="policy-days">Giorni annui</Label><Input id="policy-days" type="number" min="0.01" max="366" step="0.01" value={form.giorni_annui} onChange={(event) => update('giorni_annui', event.target.value)} required /></div>
            <div className="space-y-1.5"><Label htmlFor="policy-counting">Criterio di conteggio</Label><select id="policy-counting" value={form.criterio_conteggio} onChange={(event) => update('criterio_conteggio', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"><option value="5_GIORNI">Settimana di 5 giorni</option><option value="6_GIORNI">Settimana di 6 giorni</option><option value="CALENDARIO">Giorni di calendario</option><option value="PERSONALIZZATO">Personalizzato</option></select></div>
            <div className="space-y-1.5"><Label htmlFor="policy-calendar">Calendario ferie applicato</Label><select id="policy-calendar" value={form.calendario_id} onChange={(event) => update('calendario_id', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"><option value="">Nessun calendario configurato</option>{calendars.map((calendar) => <option key={calendar.id} value={calendar.id}>{calendar.nome} · {calendar.anno} · {calendar.fonte}</option>)}</select></div>
            <div className="space-y-1.5"><Label htmlFor="policy-workweek">Settimana lavorativa</Label><select id="policy-workweek" value={form.settimana_lavorativa} onChange={(event) => update('settimana_lavorativa', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"><option value="5">5 giorni</option><option value="6">6 giorni</option></select></div>
            <div className="space-y-1.5"><Label htmlFor="policy-start">Valida dal</Label><Input id="policy-start" type="date" value={form.data_inizio_validita} onChange={(event) => update('data_inizio_validita', event.target.value)} required /></div>
            <div className="space-y-1.5"><Label htmlFor="policy-end">Valida fino al (facoltativo)</Label><Input id="policy-end" type="date" value={form.data_fine_validita} onChange={(event) => update('data_fine_validita', event.target.value)} /></div>
            <div className="space-y-1.5"><Label htmlFor="policy-ccnl">CCNL / codice</Label><Input id="policy-ccnl" value={form.ccnl} onChange={(event) => update('ccnl', event.target.value)} placeholder="Solo se documentato" /></div>
            <div className="space-y-1.5"><Label htmlFor="policy-code">Codice CCNL</Label><Input id="policy-code" value={form.codice_ccnl} onChange={(event) => update('codice_ccnl', event.target.value)} /></div>
            <div className="space-y-1.5 md:col-span-2"><Label htmlFor="policy-description">Descrizione / note</Label><Input id="policy-description" value={form.descrizione} onChange={(event) => update('descrizione', event.target.value)} placeholder="Riferimento alla documentazione approvata" /></div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button type="submit" disabled={saving} className="gap-2 bg-brand-green text-white hover:bg-brand-green-hover">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva policy</Button>
            <Button type="button" variant="outline" onClick={() => setOpenForm(false)} disabled={saving}>Annulla</Button>
          </div>
        </form>
      )}
    </SettingsLayout>
  );
}